import { useEffect, useMemo, useState } from 'react'

import { useForm } from 'react-hook-form'
import {
  Box,
  Button,
  FormControl,
  FormErrorMessage,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Text,
  Textarea,
  Tooltip,
} from '@chakra-ui/react'

import { isWorkEmail } from '@/helpers/team-name/is-work-email'
import {
  useCompany,
  useCreateCompany,
  useGenerateCompanyDescription,
  useUpdateCompany,
} from '@/react-query/company'
import { useUser } from '@/react-query/user'
import { MagicStarsIcon } from '@/views/assets/MagicStarsIcon'
import { MarkdownRenderer } from '@/views/components/MarkdownRenderer/MarkdownRenderer'

interface ICompanyForm {
  name: string
  description: string
}

export const EditCompanyModal = ({
  isOpen,
  onClose,
}: {
  isOpen: boolean
  onClose: () => void
}) => {
  const createCompany = useCreateCompany()
  const updateCompany = useUpdateCompany()
  const company = useCompany()
  const generateCompanyDescription = useGenerateCompanyDescription()
  const { data: user } = useUser()

  const generateCompanyName = (email?: string) => {
    if (email && isWorkEmail(email)) {
      return email.split('@')[1]
    }
    return ''
  }

  const defaultName = useMemo(() => {
    return company.data?.name || generateCompanyName(user?.email) || ''
  }, [company.data?.name, user?.email])

  const {
    handleSubmit,
    clearErrors,
    formState: { errors, isSubmitting },
    register,
    reset,
    watch,
  } = useForm<ICompanyForm>({
    defaultValues: {
      name: defaultName,
      description: company.data?.description || '',
    },
  })

  // Reset the form when modal opens
  useEffect(() => {
    if (isOpen) {
      reset({
        name: defaultName,
        description: company.data?.description || '',
      })
      clearErrors()
    }
  }, [company.data?.id, isOpen, defaultName, reset, clearErrors])

  const createOrUpdateCompany = async ({ name, description }: ICompanyForm) => {
    try {
      if (company.data?.id) {
        updateCompany.mutate({
          company: {
            id: company.data.id,
            name,
            description,
          },
        })
      } else {
        await createCompany.mutateAsync({
          company: {
            name,
            description,
          },
        })
      }
      onClose()
    } catch (err) {
      console.error(err)
    }
  }

  const [isEditing, setIsEditing] = useState(false)

  const currentDescription = watch('description')

  return (
    <Modal size={'3xl'} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <form onSubmit={handleSubmit(createOrUpdateCompany)}>
          <ModalHeader>
            {company.data?.id ? 'Update company' : 'New company'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody display={'flex'} flexDirection={'column'}>
            <FormControl isInvalid={Boolean(errors.name)}>
              <Input
                id="name"
                w={'xs'}
                rounded={'1.25rem'}
                placeholder="Company name or website"
                {...register('name', {
                  required: 'Company name is required',
                })}
                variant={'brand-solid-white'}
              />
              <FormErrorMessage>
                {errors.name && errors.name.message}
              </FormErrorMessage>
            </FormControl>
            <Box
              mt={8}
              display={'flex'}
              flexDirection={'column'}
              gap={2}
              alignItems={'flex-start'}
            >
              <Tooltip
                rounded={'1.25rem'}
                py={2}
                px={4}
                bgColor={'blackAlpha.900'}
                label={'Please enter a company name first'}
                isDisabled={!!watch('name')}
                shouldWrapChildren={true}
              >
                <Button
                  variant={'brand-solid'}
                  leftIcon={<MagicStarsIcon />}
                  isLoading={generateCompanyDescription.isPending}
                  disabled={
                    !watch('name') || generateCompanyDescription.isPending
                  }
                  onClick={async () => {
                    const name = watch('name')
                    if (!name) return
                    const description =
                      await generateCompanyDescription.mutateAsync({
                        companyName: name,
                      })
                    reset({
                      name,
                      description,
                    })
                  }}
                >
                  Generate description
                </Button>
                {currentDescription && (
                  <Button
                    variant={'brand-solid-white'}
                    onClick={() => setIsEditing(!isEditing)}
                    ml={2}
                    isDisabled={generateCompanyDescription.isPending}
                  >
                    {isEditing ? 'Save' : 'Edit...'}
                  </Button>
                )}
              </Tooltip>
              {(isEditing || !currentDescription) && (
                <Text fontSize={'sm'} ml={2}>
                  Or input your own description:
                </Text>
              )}
            </Box>

            <FormControl isInvalid={Boolean(errors.description)} mt={2}>
              <Box>
                {isEditing || !currentDescription ? (
                  <Textarea
                    id="description"
                    pt={4}
                    height={'25rem'}
                    placeholder="Company description"
                    resize="none"
                    rounded={'1.25rem'}
                    variant={'brand-solid-white'}
                    {...register('description', {
                      required: 'Company description is required',
                    })}
                  />
                ) : (
                  <MarkdownRenderer
                    text={currentDescription}
                    className="flex-grow"
                  />
                )}
              </Box>
              <FormErrorMessage>
                {errors.description && errors.description.message}
              </FormErrorMessage>
            </FormControl>
          </ModalBody>

          <ModalFooter>
            <Button
              type="submit"
              variant={'brand-solid'}
              isLoading={isSubmitting}
              rounded={'1.25rem'}
              mr={2}
            >
              {company.data?.id ? 'Update' : 'Create'}
            </Button>
            <Button
              rounded={'1.25rem'}
              variant="brand-solid-white"
              onClick={onClose}
            >
              Cancel
            </Button>
          </ModalFooter>
        </form>
      </ModalContent>
    </Modal>
  )
}
